// Shared per-record posting date/time form for Studio and Scheduling.
// Each mounted instance owns its inputs and submits only its saved post ID.
// Uses local-time inputs, future-date validation, approval confirmation and
// owning-page notifications; completed/publishing records remain read-only.
import React, { useEffect, useRef, useState } from "react";
import { canSchedulePromotionPost } from "../helpers/promotion_workflow";

const localInputs = (value) => {
  if (!value) return { date: "", time: "" };
  const date = typeof value.toDate === "function" ? value.toDate() : new Date(value);
  if (Number.isNaN(date.getTime())) return { date: "", time: "" };
  const pad = (number) => String(number).padStart(2, "0");
  return { date: `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`, time: `${pad(date.getHours())}:${pad(date.getMinutes())}` };
};

export default function PromotionScheduleForm({ post, onSchedule, onNotification, disabled = false, schedulerEnabled = true }) {
  const saved = localInputs(post?.scheduledAt);
  const [values, setValues] = useState(saved);
  const [busy, setBusy] = useState(false);
  const inFlight = useRef(false);
  // Timestamp instances change identity on Firestore refresh. Compare their
  // actual local values so another row's save cannot discard this row's edits.
  useEffect(() => { setValues({ date: saved.date, time: saved.time }); }, [post?.id, saved.date, saved.time]);
  if (!post?.id || !canSchedulePromotionPost(post)) return null;
  const needsApproval = post.queueStatus !== "approved";
  const identity = `${post.platform || "Social"} / ${post.requestType === "social_post" || !post.requestType ? "Social Post" : post.requestType}`;
  const notice = (type, message) => onNotification?.({ show: true, type, message });
  const submit = async (event) => {
    event.preventDefault();
    if (disabled || busy || inFlight.current || !schedulerEnabled) return;
    if (!values.date || !values.time) { notice("warning", "Select both a posting date and time."); return; }
    const date = new Date(`${values.date}T${values.time}:00`);
    if (Number.isNaN(date.getTime()) || date <= new Date()) { notice("warning", "Select a future posting date and time."); return; }
    if (!window.confirm(`${needsApproval ? "Approve and schedule" : "Schedule"} ${identity} for ${date.toLocaleString()}?`)) return;
    inFlight.current = true; setBusy(true);
    try {
      await onSchedule({ postId: post.id, scheduledAt: date.toISOString() });
      notice("success", "Promotion record scheduled.");
    } catch (error) { notice("error", error.message || "Unable to schedule promotion record."); }
    finally { inFlight.current = false; setBusy(false); }
  };
  return <form noValidate onSubmit={submit} aria-label={`Schedule ${identity} (${post.id})`}>
    <div className="d-flex flex-wrap align-items-end gap-2">
      <div><label className="form-label small mb-1" htmlFor={`posting-date-${post.id}`}>Posting date</label>
        <input id={`posting-date-${post.id}`} type="date" required className="form-control form-control-sm" value={values.date}
          disabled={disabled || busy || !schedulerEnabled} onChange={(event) => setValues((current) => ({ ...current, date: event.target.value }))} /></div>
      <div><label className="form-label small mb-1" htmlFor={`posting-time-${post.id}`}>Posting time</label>
        <input id={`posting-time-${post.id}`} type="time" required className="form-control form-control-sm" value={values.time}
          disabled={disabled || busy || !schedulerEnabled} onChange={(event) => setValues((current) => ({ ...current, time: event.target.value }))} /></div>
      <button type="submit" className="btn btn-sm btn-primary" disabled={disabled || busy || !schedulerEnabled}>
        {busy ? "Scheduling..." : needsApproval ? "Approve & Schedule" : post.publishStatus === "scheduled" ? "Update Schedule" : "Schedule"}
      </button>
    </div>
    <div className="form-text">{identity}. Times use {Intl.DateTimeFormat().resolvedOptions().timeZone}.</div>
  </form>;
}
